import { useState, useCallback, useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import Drawer from '~/components/Drawer';
import { inputStyles } from '~/components/Vendors/styles';
import { fetchPatientServicesCoordinators } from '~/services/campaign';

interface CSCOption {
    id: number;
    full_name: string;
    first_name?: string;
    last_name?: string;
}

interface SelectOption {
    value: number;
    label: string;
}

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    currentCSC?: CSCOption | null;
    handleSaveCSC: (csc: CSCOption) => void;
    saving: boolean;
}

const AssignCSCDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    currentCSC,
    handleSaveCSC,
    saving,
}) => {
    const [selectedCSC, setSelectedCSC] = useState<SelectOption | null>(null);

    // Reset selection when drawer opens
    useEffect(() => {
        if (isOpen && currentCSC) {
            setSelectedCSC({
                value: currentCSC.id,
                label: currentCSC.full_name || `${currentCSC.first_name} ${currentCSC.last_name}`,
            });
        } else if (isOpen) {
            setSelectedCSC(null);
        }
    }, [isOpen, currentCSC]);

    // Load CSC options from the API
    const loadCSCOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
        try {
            // Only search if input is at least 2 characters or empty (for initial load)
            if (inputValue.length > 0 && inputValue.length < 2) {
                return [];
            }
            
            const response = await fetchPatientServicesCoordinators(inputValue || undefined);
            
            // Handle paginated response format from Django REST framework
            const dataArray = response?.results || response || [];
            
            if (!Array.isArray(dataArray)) {
                console.error('Expected array or paginated response, got:', response);
                return [];
            }
            
            return dataArray.map((psc: CSCOption) => ({
                value: psc.id,
                label: psc.full_name,
            }));
        } catch (error) {
            console.error('Error fetching Client Services Coordinators:', error);
            return [];
        }
    }, []);

    const resetAndCloseForm = () => {
        setSelectedCSC(null);
        setIsOpen(false);
    };

    const handleSave = () => {
        if (selectedCSC) {
            handleSaveCSC({
                id: selectedCSC.value,
                full_name: selectedCSC.label,
            });
            resetAndCloseForm();
        }
    };

    return (
        <Drawer isOpen={isOpen} setIsOpen={setIsOpen}>
            <h2 className='mb-[28px] text-[22px]/[28px] font-bold'>
                {currentCSC ? 'Reassign' : 'Assign'} Client Services Coordinator
            </h2>

            <label
                htmlFor='csc-select'
                className='block font-bold'
            >
                Client Services Coordinator <span className='text-red'>*</span>
            </label>
            <AsyncSelect
                id='csc-select'
                value={selectedCSC}
                onChange={(newValue) => setSelectedCSC(newValue)}
                loadOptions={loadCSCOptions}
                defaultOptions
                placeholder="Search for a coordinator..."
                noOptionsMessage={({ inputValue }) => {
                    if (!inputValue) return 'Type to search coordinators';
                    if (inputValue.length < 2) return 'Type at least 2 characters to search';
                    return `No coordinators found for "${inputValue}"`;
                }}
                isClearable
                isSearchable
                cacheOptions
                className='mb-4'
                styles={{
                    control: (base) => ({
                        ...base,
                        backgroundColor: '#f3f4f6',
                        borderColor: '#d1d5db',
                    }),
                }}
            />

            <div className='mt-[30px]'>
                <button
                    className='m-auto mr-3 justify-self-end rounded-3xl border-[2px] border-black bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase hover:bg-black hover:text-white'
                    onClick={resetAndCloseForm}
                >
                    Cancel
                </button>
                <button
                    className='m-auto mr-3 rounded-full bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-[#131212] hover:bg-opacity-90'
                    onClick={handleSave}
                    disabled={!selectedCSC || saving}
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </Drawer>
    );
};

export default AssignCSCDrawer;
